import React, { useState } from 'react';
import { FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Loader from '../Utility/Loader/Loader';
import { toasterMsg } from '../Toaster/Toaster';
import CustomTooltip from '../Tooltip/CustomTooltip';
import { NewInput } from '../PIM/common/NewInput';
import { AddIcon, DeleteIcon, CloseIcon } from '../../bombiix-icon';
import { BombiixModal } from '../BombiixModal';

function AddMultiDropOpt({ onOpen, onSubmit, toggle, loading, bomTypeId, multiDynamicAttribute, extraClass = '' }) {
  const { t } = useTranslation('common');
  const [isDesabled, setIsDesabled] = useState(false);
  const [componentTypeList, setComponentTypeList] = useState([
    {
      keyName: '',
      isEmpty: false,
      isActive: true,
      _id: '',
    },
  ]);

  const handleAdded = async () => {
    const dataObj = [
      ...componentTypeList,
      {
        keyName: '',
        isEmpty: false,
        isActive: true,
      },
    ];
    setComponentTypeList(dataObj);
  };

  const handleRemove = (index) => {
    let rows = [...componentTypeList];
    rows.splice(index, 1);
    if (rows.length === 0) {
      rows = [
        {
          keyName: '',
          isEmpty: false,
          isActive: true,
        },
      ];
    }
    setComponentTypeList(rows);
  };
  const title = multiDynamicAttribute?.find((e) => e?._id === bomTypeId)?.keyName || '';

  const handleChange = (event, index) => {
    const fieldName = event?.currentTarget?.name || '';
    const value = event?.currentTarget?.value?.trimStart() || '';
    if (value.length < 50) {
      const rows = [...componentTypeList];
      let duplicateFound = false;

      rows.forEach((item, idx) => {
        if (idx !== index && item.keyName.toLowerCase().trim() === value.toLowerCase().trim()) {
          duplicateFound = true;
        }
      });

      if (duplicateFound) {
        const updatedRows = rows.map((item, idx) => {
          if (idx === index) {
            return {
              ...item,
              [fieldName]: value,
              isEmpty: value === '',
              isDuplicate: true,
            };
          }
          return item;
        });
        setComponentTypeList(updatedRows);
      } else {
        const updatedRows = rows.map((item, idx) => {
          if (idx === index) {
            return {
              ...item,
              [fieldName]: value,
              isEmpty: value === '',
              isDuplicate: false,
            };
          }
          return item;
        });
        setComponentTypeList(updatedRows);
      }
    }
  };

  const saveComponentList = async () => {
    setIsDesabled(true);

    if (componentTypeList?.length > 0) {
      const hasDuplicate = componentTypeList.some((item) => item?.isDuplicate);

      if (!hasDuplicate) {
        const emptyField = componentTypeList.filter((item) => item.keyName.trim() === '');

        if (emptyField.length === 0) {
          const labelData = multiDynamicAttribute.find((item) => item._id === bomTypeId);
          const existingValues = labelData?.values.map((v) => v.keyName) || [];
          const newOptions = componentTypeList
            .filter((item) => item.keyName && !item.isDuplicate)
            .map((item) => ({
              isActive: true,
              keyName: item.keyName,
              lableId: labelData?._id,
            }));

          const duplicateValues = newOptions.filter((option) => existingValues.includes(option.keyName));

          if (duplicateValues.length > 0) {
            toasterMsg(`The following values already exist: ${duplicateValues.map((v) => v.keyName).join(', ')}`, 'danger');
            setIsDesabled(false);
            return;
          }

          if (newOptions.length) {
            const updatedData = multiDynamicAttribute.map((item) => {
              if (item._id === bomTypeId) {
                return {
                  ...item,
                  values: [...item.values, ...newOptions],
                };
              }
              return item;
            });

            onSubmit(updatedData, newOptions);
          }
        } else {
          const newState = componentTypeList.map((obj) => (obj.isEmpty === false && obj.keyName.trim() === '' ? { ...obj, isEmpty: true } : obj));
          setComponentTypeList(newState);
        }
      }
    }
    setIsDesabled(false);
  };

  return (
    <BombiixModal
      show={onOpen}
      className={extraClass}
      title={`Add ${title} dropdown options`}
      onClose={toggle}
      onSave={() => saveComponentList()}
      saveLabel={t('COMMON_LABEL.SAVE')}
      saveDisabled={isDesabled}
    >
      {loading && <Loader />}
      {!loading && (
        <div className="modal-body-content">
            {componentTypeList?.length > 0
              ? componentTypeList.map((item, i) => {
                  return (
                    <div key={i} className="mb-3">
                      <div className="d-flex w-100 align-items-end" style={{ gap: 8 }}>
                        <FormGroup className="mb-0 flex-grow-1">
                          <NewInput
                            label={t('MULTI_CUSTOM_ATTRIBUTES.ADD_MULTI_DROP_DOWN_OPTIONS')}
                            name="keyName"
                            value={item.keyName ? item.keyName : ''}
                            onChange={(e) => handleChange(e, i)}
                            placeholder={t('MULTI_CUSTOM_ATTRIBUTES.ADD_MULTI_DROP_DOWN_OPTIONS')}
                            invalid={item?.isDuplicate || item?.isEmpty}
                          />
                        </FormGroup>

                        {componentTypeList.length === i + 1 && (
                          <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.ADD')}>
                            <button
                              onClick={() => {
                                handleAdded();
                              }}
                              type="button"
                              aria-label="add"
                              className="bombiix-icon-container bombiix-icon-container--blue"
                            >
                              <AddIcon size={20} />
                            </button>
                          </CustomTooltip>
                        )}
                        {componentTypeList.length === 1 ? (
                          <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.CLEAR')}>
                            <button
                              onClick={() => {
                                handleRemove(i);
                              }}
                              type="button"
                              aria-label="delete"
                              className="bombiix-icon-container bombiix-icon-container--red"
                              name="Clear"
                            >
                              <CloseIcon size={20} />
                            </button>
                          </CustomTooltip>
                        ) : (
                          <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.DELETE')}>
                            <button
                              onClick={() => {
                                handleRemove(i);
                              }}
                              type="button"
                              aria-label="delete"
                              className="bombiix-icon-container bombiix-icon-container--red"
                            >
                              <DeleteIcon size={20} />
                            </button>
                          </CustomTooltip>
                        )}
                      </div>
                      {item?.isDuplicate && <span className="text-danger f-14 m-0 pl-0 p-1">{t('SET_COMPONENET_TYPE.ENTRY_ALREADY_EXISTS')}</span>}{' '}
                      {item?.isEmpty && <span className="text-danger f-14 m-0 pl-0 m-0 p-1"> options is required</span>}
                    </div>
                  );
                })
              : null}
        </div>
      )}
    </BombiixModal>
  );
}

export default AddMultiDropOpt;
